import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, Linkedin, ShieldCheck, Lock, Terminal, ArrowRight, Signal } from 'lucide-react';
import SEO from '../components/SEO';

export const Contact = () => {
  const { t } = useTranslation();
  const [isConnecting, setIsConnecting] = useState(true);
  // const [matrixEffect, setMatrixEffect] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
        setIsConnecting(false);
        // setMatrixEffect(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/393806331704', '_blank');
  };

  const handleLinkedInClick = () => {
    window.open('https://www.linkedin.com/in/victor-lazari/', '_blank');
  };

  return (
    <div className="relative min-h-screen bg-slate-950 text-slate-200 pt-32 pb-20 px-6 overflow-hidden font-sans selection:bg-cyan-500/30">
      {/* Cyberpunk Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.03)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_40%,transparent_100%)] pointer-events-none" />
      
      {/* Ambient Glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />

      <SEO
        title={`${t('navigation.contact') || 'Contact'} – Victor Lazari`}
        description={t('contact.info.title') as string}
        canonical={"https://victorlazari.com/contact"}
        image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
        type="website"
      />

      <div className="relative max-w-7xl mx-auto w-full z-10">
        
        {/* Status Console */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-slate-900/80 border border-slate-800 backdrop-blur-md text-xs font-mono mb-8 shadow-2xl"
          >
             <div className={`w-2 h-2 rounded-full ${isConnecting ? 'bg-yellow-500 animate-ping' : 'bg-emerald-500'}`}></div>
             <span className={isConnecting ? 'text-yellow-500' : 'text-emerald-500'}>
                {isConnecting ? t('contact.hero.connecting') : t('contact.hero.connected')}
             </span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-8xl font-black text-white mb-8 tracking-tighter leading-none"
          >
            {t('contact.hero.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">{t('contact.hero.titleHighlight')}</span>
          </motion.h1>
          
          <motion.p 
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             transition={{ delay: 0.4 }}
             className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
          >
            {t('contact.hero.subtitle')} 
            <span className="text-white font-medium"> {t('contact.hero.subtitleHighlight')}</span>
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Access Methods */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-6"
          >
             {/* Main Card */}
             <div className="bg-slate-900/50 border border-slate-800 rounded-[2rem] p-8 backdrop-blur-xl relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                    <div className="flex items-center justify-between mb-8">
                        <h3 className="text-xl font-bold text-white flex items-center gap-3">
                        <Terminal size={20} className="text-cyan-500" /> {t('contact.access.title')}
                        </h3>
                        <Lock size={16} className="text-slate-600" />
                    </div>

                    <div className="space-y-4">
                        {/* WhatsApp - The Priority Line */}
                        <div 
                            onClick={handleWhatsAppClick}
                            className="relative group/item cursor-pointer overflow-hidden rounded-xl"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 opacity-10 group-hover/item:opacity-20 transition-opacity" />
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-cyan-500" />
                            
                            <div className="relative p-5 flex items-center gap-5 border border-cyan-500/20 rounded-xl hover:border-cyan-500/50 transition-all">
                                <div className="w-12 h-12 rounded-lg bg-cyan-950/50 flex items-center justify-center text-cyan-400 group-hover/item:scale-110 transition-transform">
                                    <Phone size={24} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-500 bg-cyan-950/50 px-2 py-0.5 rounded">{t('contact.access.whatsapp.priority')}</span>
                                        <span className="text-[10px] text-slate-500 font-mono">{t('contact.access.whatsapp.responseTime')}</span>
                                    </div>
                                    <div className="text-lg font-bold text-white group-hover/item:text-cyan-400 transition-colors">{t('contact.access.whatsapp.label')}</div>
                                </div>
                                <ArrowRight className="ml-auto text-slate-600 group-hover/item:text-cyan-500 transition-colors" size={20} />
                            </div>
                        </div>

                        {/* Email */}
                        <div className="relative group/item rounded-xl border border-slate-800 hover:border-slate-700 bg-slate-950/30 transition-all p-5 flex items-center gap-5">
                            <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center text-slate-500 group-hover/item:text-white transition-colors">
                                <Mail size={24} />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-1">{t('contact.access.email.label')}</div>
                                <div className="text-lg font-bold text-slate-300 group-hover/item:text-white transition-colors">{t('contact.info.email.address')}</div>
                            </div>
                        </div>

                        {/* LinkedIn */}
                        <div 
                            onClick={handleLinkedInClick}
                            className="relative group/item cursor-pointer rounded-xl border border-slate-800 hover:border-blue-500/30 bg-slate-950/30 hover:bg-blue-900/10 transition-all p-5 flex items-center gap-5"
                        >
                            <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center text-slate-500 group-hover/item:text-blue-400 transition-colors">
                                <Linkedin size={24} />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-1">{t('contact.access.linkedin.label')}</div>
                                <div className="text-lg font-bold text-slate-300 group-hover/item:text-white transition-colors">Victor Lazari</div>
                            </div>
                             <ArrowRight className="ml-auto text-slate-600 group-hover/item:text-blue-500 transition-colors" size={20} />
                        </div>
                    </div>
                </div>
             </div>

             {/* Status Indicator */}
             <div className="bg-slate-900/30 border border-slate-800 rounded-2xl p-6 flex items-start gap-4">
                <div className="relative">
                    <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-20 animate-ping" />
                    <div className="relative w-3 h-3 rounded-full bg-emerald-500 mt-1.5" />
                </div>
                <div>
                    <h4 className="text-white font-bold text-sm mb-2">{t('contact.status.title')}</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                        {t('contact.status.description')}
                    </p>
                </div>
             </div>
          </motion.div>

          {/* Right Column: Global Presence / Map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="relative h-[600px] bg-slate-900 rounded-[2rem] border border-slate-800 overflow-hidden group shadow-2xl"
          >
            {/* HUD Overlay */}
            <div className="absolute top-8 left-8 z-20">
               <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-950/80 backdrop-blur border border-slate-700 rounded-lg mb-4">
                  <Signal size={12} className="text-cyan-500 animate-pulse" />
                  <span className="text-[10px] font-mono text-cyan-500">{t('contact.map.signal')}</span>
               </div>
               <div className="text-4xl font-black text-white mb-1">{t('contact.map.location')}</div>
               <div className="text-sm font-mono text-slate-400 uppercase tracking-widest">{t('contact.map.locationSubtitle')}</div>
            </div>

            <div className="absolute bottom-8 left-8 z-20 max-w-xs">
               <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center">
                     <ShieldCheck size={14} className="text-emerald-500" />
                  </div>
                  <div className="text-xs font-bold text-white">{t('contact.map.secureFacility')}</div>
               </div>
               <p className="text-xs text-slate-500 leading-relaxed">
                  {t('contact.map.secureDescription')}
               </p>
            </div>

            {/* The Map */}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2852.5792927240777!2d12.096704576918982!3d44.3843947104402!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477e0aa30d1d24ef%3A0xb6685f24df8eae8!2s48125%20San%20Pietro%20in%20Vincoli%20RA%2C%20Italy!5e0!3m2!1sen!2sus!4v1711054023457!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(95%) hue-rotate(180deg) contrast(90%) grayscale(30%) brightness(0.7)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="opacity-50 group-hover:opacity-70 transition-all duration-700 scale-105 group-hover:scale-100"
              title={t('contact.map.title')}
            />
            
            {/* Grid Overlay */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none mix-blend-overlay" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none opacity-20" />

            {/* Scanning Line */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/10 to-transparent h-[20%] w-full animate-scan pointer-events-none border-b border-cyan-500/20" />
          </motion.div>

        </div>
      </div>
      
      <style>{`
        @keyframes scan {
          0% { top: -20%; }
          100% { top: 120%; }
        }
        .animate-scan {
          animation: scan 4s linear infinite;
        }
      `}</style>
    </div>
  );
};
